/**
 * Shared Projects transport (#1388). Every read is a typed TanStack query over the generated client; every write
 * only proposes or edits the server contract it names. Confirming a returned card still goes through the ordinary
 * card endpoint, so project screens never grow local executors.
 *
 * Money fields are deliberately not normalised here. `role_response` removes them for roles that cannot read them,
 * and consumers must preserve that distinction between absent, null/unknown and a real zero.
 */
import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query'
import { ApiError, unwrap } from '../../lib/api/client'
import * as generated from '../../lib/api/generated'

type OptionsOf<T extends (...args: never[]) => unknown> = Parameters<T>[0]

type ProjectListQuery = NonNullable<generated.ListProjectsApiV1ProjectsGetData['query']>
export type ProjectListFilters = Omit<ProjectListQuery, 'cursor'>
export type ProjectLane = NonNullable<ProjectListFilters['lane']>
export type ProjectLaneSlug =
  | 'needs-my-move'
  | 'waiting-customer'
  | 'waiting-contractor'
  | 'waiting-vendor'
  | 'scheduled'
  | 'on-hold'
  | 'closing-this-month'
  | 'closed-30d'
  | 'all'

export interface ProjectLaneDefinition {
  lane: ProjectLane
  slug: ProjectLaneSlug
  label: string
}

export const PROJECT_LANES: readonly ProjectLaneDefinition[] = [
  { lane: 'needs_my_move', slug: 'needs-my-move', label: 'Needs my move' },
  { lane: 'waiting_customer', slug: 'waiting-customer', label: 'Waiting on customer' },
  { lane: 'waiting_contractor', slug: 'waiting-contractor', label: 'Waiting on contractor' },
  { lane: 'waiting_vendor', slug: 'waiting-vendor', label: 'Waiting on vendor / parts' },
  { lane: 'scheduled', slug: 'scheduled', label: 'Scheduled' },
  { lane: 'on_hold', slug: 'on-hold', label: 'On hold' },
  { lane: 'closing_this_month', slug: 'closing-this-month', label: 'Closing this month' },
  { lane: 'closed_30d', slug: 'closed-30d', label: 'Closed (30d)' },
  { lane: 'all', slug: 'all', label: 'All projects' },
]

export function projectLaneFromSlug(value: string): ProjectLaneDefinition | undefined {
  return PROJECT_LANES.find((definition) => definition.slug === value)
}

export function projectLaneDefinition(value: ProjectLane): ProjectLaneDefinition {
  const definition = PROJECT_LANES.find((candidate) => candidate.lane === value)
  if (!definition) throw new Error(`unknown project lane ${value}`)
  return definition
}

export function canSeeProjects(role: string | null | undefined): boolean {
  return role === 'manager' || role === 'cover' || role === 'billing'
}

export const projectKeys = {
  all: ['projects'] as const,
  lanes: () => ['projects', 'lanes'] as const,
  lists: () => ['projects', 'list'] as const,
  list: (filters: ProjectListFilters) => ['projects', 'list', filters] as const,
  portfolio: (filters: ProjectListFilters) => ['projects', 'portfolio', filters] as const,
  account: (accountId: string) => ['projects', 'account', accountId] as const,
  detail: (projectId: string) => ['projects', 'detail', projectId] as const,
  facts: (projectId: string) => ['projects', 'detail', projectId, 'facts'] as const,
  activity: (projectId: string, query: generated.ProjectActivityApiV1ProjectsProjectIdActivityGetData['query']) =>
    ['projects', 'detail', projectId, 'activity', query ?? {}] as const,
  strip: (caseId: string) => ['projects', 'case', caseId, 'strip'] as const,
  tasks: (query: generated.ListTasksApiV1ProjectsTasksGetData['query']) => ['projects', 'tasks', query ?? {}] as const,
  plan: (projectId: string) => ['projects', 'detail', projectId, 'plan'] as const,
  gantt: (projectId: string) => ['projects', 'detail', projectId, 'gantt'] as const,
  milestones: (projectId: string) => ['projects', 'detail', projectId, 'milestones'] as const,
  dependencies: (projectId: string) => ['projects', 'detail', projectId, 'dependencies'] as const,
  linkedCases: (projectId: string) => ['projects', 'detail', projectId, 'linked-cases'] as const,
  budget: (projectId: string) => ['projects', 'detail', projectId, 'budget'] as const,
  changeOrders: (projectId: string) => ['projects', 'detail', projectId, 'change-orders'] as const,
  templates: (query: generated.ListTemplatesApiV1ProjectsTemplatesGetData['query']) => ['projects', 'templates', query ?? {}] as const,
  template: (templateId: string) => ['projects', 'templates', templateId] as const,
  templateExport: (templateId: string) => ['projects', 'templates', templateId, 'export'] as const,
  meetings: (caseId: string) => ['projects', 'case', caseId, 'meetings'] as const,
  meeting: (meetingId: string) => ['projects', 'meetings', meetingId] as const,
  files: (caseId: string) => ['projects', 'case', caseId, 'files'] as const,
  vendorContacts: (query: generated.ListVendorContactsApiV1VendorContactsGetData['query']) =>
    ['projects', 'vendor-contacts', query ?? {}] as const,
  pmOptions: () => ['projects', 'pm-options'] as const,
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/** The generated type is compile-time only; keep malformed gateway answers out of every Projects consumer. */
export function parseProjectLanes(value: unknown): generated.ProjectLanesOut {
  if (
    !isRecord(value) ||
    !Array.isArray(value.lanes) ||
    !value.lanes.every(
      (lane) =>
        isRecord(lane) &&
        typeof lane.lane === 'string' &&
        typeof lane.label === 'string' &&
        (typeof lane.count === 'number' || lane.count === 'unknown'),
    ) ||
    !isRecord(value.footer) ||
    !(typeof value.footer.open_count === 'number' || value.footer.open_count === 'unknown')
  ) {
    throw new Error('project lanes answer malformed')
  }
  return value as generated.ProjectLanesOut
}

export const projectLanesQuery = () =>
  queryOptions({
    queryKey: projectKeys.lanes(),
    queryFn: async ({ signal }): Promise<generated.ProjectLanesOut> =>
      parseProjectLanes(await unwrap(generated.projectLanesApiV1ProjectsLanesGet({ signal }))),
    staleTime: 30_000,
    retry: 1,
  })

export const projectListQuery = (filters: ProjectListFilters) =>
  queryOptions({
    queryKey: projectKeys.list(filters),
    queryFn: ({ signal }): Promise<generated.PageProjectRow> => unwrap(generated.listProjectsApiV1ProjectsGet({ query: filters, signal })),
    staleTime: 30_000,
    retry: 1,
  })

export const projectListInfiniteQuery = (filters: ProjectListFilters) =>
  infiniteQueryOptions({
    queryKey: projectKeys.portfolio(filters),
    queryFn: ({ pageParam, signal }): Promise<generated.PageProjectRow> =>
      unwrap(
        generated.listProjectsApiV1ProjectsGet({
          query: { ...filters, cursor: pageParam },
          signal,
        }),
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.next_cursor ?? undefined,
    staleTime: 30_000,
    retry: 1,
  })

export const accountProjectsQuery = (accountId: string) =>
  queryOptions({
    queryKey: projectKeys.account(accountId),
    queryFn: ({ signal }): Promise<generated.ProjectRow[]> =>
      unwrap(generated.accountProjectsApiV1AccountsAccountIdProjectsGet({ path: { account_id: accountId }, signal })),
    staleTime: 30_000,
    retry: 1,
  })

export const projectQuery = (projectId: string) =>
  queryOptions({
    queryKey: projectKeys.detail(projectId),
    queryFn: ({ signal }): Promise<generated.ProjectOut> =>
      unwrap(generated.getProjectApiV1ProjectsProjectIdGet({ path: { project_id: projectId }, signal })),
    staleTime: 30_000,
    retry: 1,
  })

export const projectFactsQuery = (projectId: string) =>
  queryOptions({
    queryKey: projectKeys.facts(projectId),
    queryFn: ({ signal }): Promise<generated.FactsOut> =>
      unwrap(generated.projectFactsApiV1ProjectsProjectIdFactsGet({ path: { project_id: projectId }, signal })),
    staleTime: 30_000,
    retry: 1,
  })

export const projectActivityQuery = (
  projectId: string,
  query: generated.ProjectActivityApiV1ProjectsProjectIdActivityGetData['query'] = {},
) =>
  queryOptions({
    queryKey: projectKeys.activity(projectId, query),
    queryFn: ({ signal }): Promise<generated.ProjectActivityItem[]> =>
      unwrap(generated.projectActivityApiV1ProjectsProjectIdActivityGet({ path: { project_id: projectId }, query, signal })),
    staleTime: 15_000,
    retry: 1,
  })

export const caseProjectStripQuery = (caseId: string) =>
  queryOptions({
    queryKey: projectKeys.strip(caseId),
    queryFn: ({ signal }): Promise<generated.ProjectStripOut> =>
      unwrap(generated.caseProjectStripApiV1CasesCaseIdProjectStripGet({ path: { case_id: caseId }, signal })),
    staleTime: 30_000,
    retry: 1,
  })

export const projectTasksQuery = (query: generated.ListTasksApiV1ProjectsTasksGetData['query'] = {}) =>
  queryOptions({
    queryKey: projectKeys.tasks(query),
    queryFn: ({ signal }): Promise<generated.TaskOut[]> => unwrap(generated.listTasksApiV1ProjectsTasksGet({ query, signal })),
    staleTime: 15_000,
    retry: 1,
  })

export const projectPlanQuery = (projectId: string) =>
  queryOptions({
    queryKey: projectKeys.plan(projectId),
    queryFn: ({ signal }): Promise<generated.PlanOut> =>
      unwrap(generated.projectPlanApiV1ProjectsProjectIdPlanGet({ path: { project_id: projectId }, signal })),
    staleTime: 15_000,
    retry: 1,
  })

export const projectGanttQuery = (projectId: string) =>
  queryOptions({
    queryKey: projectKeys.gantt(projectId),
    queryFn: ({ signal }): Promise<generated.GanttOut> =>
      unwrap(generated.projectGanttApiV1ProjectsProjectIdGanttGet({ path: { project_id: projectId }, signal })),
    staleTime: 15_000,
    retry: 1,
  })

export const projectMilestonesQuery = (projectId: string) =>
  queryOptions({
    queryKey: projectKeys.milestones(projectId),
    queryFn: ({ signal }): Promise<generated.MilestoneOut[]> =>
      unwrap(generated.listMilestonesApiV1ProjectsProjectIdMilestonesGet({ path: { project_id: projectId }, signal })),
    staleTime: 15_000,
    retry: 1,
  })

export const projectDependenciesQuery = (projectId: string) =>
  queryOptions({
    queryKey: projectKeys.dependencies(projectId),
    queryFn: ({ signal }): Promise<generated.DependencyOut[]> =>
      unwrap(generated.listDependenciesApiV1ProjectsProjectIdDependenciesGet({ path: { project_id: projectId }, signal })),
    staleTime: 15_000,
    retry: 1,
  })

export const projectLinkedCasesQuery = (projectId: string) =>
  queryOptions({
    queryKey: projectKeys.linkedCases(projectId),
    queryFn: ({ signal }): Promise<generated.LinkedCaseOut[]> =>
      unwrap(generated.linkedCasesApiV1ProjectsProjectIdLinkedCasesGet({ path: { project_id: projectId }, signal })),
    staleTime: 30_000,
    retry: 1,
  })

export const projectBudgetQuery = (projectId: string) =>
  queryOptions({
    queryKey: projectKeys.budget(projectId),
    queryFn: ({ signal }): Promise<generated.BudgetOut> =>
      unwrap(generated.projectBudgetApiV1ProjectsProjectIdBudgetGet({ path: { project_id: projectId }, signal })),
    staleTime: 30_000,
    retry: 1,
  })

export const projectChangeOrdersQuery = (projectId: string) =>
  queryOptions({
    queryKey: projectKeys.changeOrders(projectId),
    queryFn: ({ signal }): Promise<generated.ChangeOrderOut[]> =>
      unwrap(generated.listChangeOrdersApiV1ProjectsProjectIdChangeOrdersGet({ path: { project_id: projectId }, signal })),
    staleTime: 30_000,
    retry: 1,
  })

export const projectTemplatesQuery = (query: generated.ListTemplatesApiV1ProjectsTemplatesGetData['query'] = {}) =>
  queryOptions({
    queryKey: projectKeys.templates(query),
    queryFn: ({ signal }): Promise<generated.TemplateOut[]> => unwrap(generated.listTemplatesApiV1ProjectsTemplatesGet({ query, signal })),
    staleTime: 60_000,
    retry: 1,
  })

export const projectTemplateQuery = (templateId: string) =>
  queryOptions({
    queryKey: projectKeys.template(templateId),
    queryFn: ({ signal }): Promise<generated.TemplateOut> =>
      unwrap(generated.getTemplateApiV1ProjectsTemplatesTemplateIdGet({ path: { template_id: templateId }, signal })),
    staleTime: 60_000,
    retry: 1,
  })

export const projectTemplateExportQuery = (templateId: string) =>
  queryOptions({
    queryKey: projectKeys.templateExport(templateId),
    queryFn: ({ signal }): Promise<string> =>
      unwrap(generated.exportTemplateApiV1ProjectsTemplatesTemplateIdExportGet({ path: { template_id: templateId }, signal })),
    staleTime: Number.POSITIVE_INFINITY,
    retry: 1,
  })

export const caseMeetingsQuery = (caseId: string) =>
  queryOptions({
    queryKey: projectKeys.meetings(caseId),
    queryFn: ({ signal }): Promise<generated.MeetingOut[]> =>
      unwrap(generated.caseMeetingsApiV1CasesCaseIdMeetingsGet({ path: { case_id: caseId }, signal })),
    staleTime: 30_000,
    retry: 1,
  })

export const meetingQuery = (meetingId: string) =>
  queryOptions({
    queryKey: projectKeys.meeting(meetingId),
    queryFn: ({ signal }): Promise<generated.MeetingOut> =>
      unwrap(generated.getMeetingApiV1MeetingsMeetingIdGet({ path: { meeting_id: meetingId }, signal })),
    staleTime: 30_000,
    retry: 1,
  })

export const caseFilesQuery = (caseId: string) =>
  queryOptions({
    queryKey: projectKeys.files(caseId),
    queryFn: ({ signal }): Promise<generated.CaseFileOut[]> =>
      unwrap(generated.listCaseFilesApiV1CasesCaseIdFilesGet({ path: { case_id: caseId }, signal })),
    staleTime: 30_000,
    retry: 1,
  })

export const vendorContactsQuery = (query: generated.ListVendorContactsApiV1VendorContactsGetData['query'] = {}) =>
  queryOptions({
    queryKey: projectKeys.vendorContacts(query),
    queryFn: ({ signal }): Promise<generated.VendorContactOut[]> =>
      unwrap(generated.listVendorContactsApiV1VendorContactsGet({ query, signal })),
    staleTime: 60_000,
    retry: 1,
  })

export const projectPmOptionsQuery = () =>
  queryOptions({
    queryKey: projectKeys.pmOptions(),
    queryFn: ({ signal }): Promise<generated.OwnersOut> => unwrap(generated.ownersApiV1OwnersGet({ signal })),
    staleTime: 60_000,
    retry: 1,
  })

/** Writes stay unwrapped but otherwise retain the generated client's exact path/body/options contract. */
export const projectsApi = {
  proposeCreate: (options: OptionsOf<typeof generated.proposeCreateProjectApiV1ProjectsPost>) =>
    unwrap(generated.proposeCreateProjectApiV1ProjectsPost(options)),
  updateBudgetLine: (options: OptionsOf<typeof generated.updateBudgetLineApiV1ProjectsBudgetLinesLineIdPatch>) =>
    unwrap(generated.updateBudgetLineApiV1ProjectsBudgetLinesLineIdPatch(options)),
  proposeApproveChangeOrder: (
    options: OptionsOf<typeof generated.proposeApproveChangeOrderApiV1ProjectsChangeOrdersChangeOrderIdApprovePost>,
  ) => unwrap(generated.proposeApproveChangeOrderApiV1ProjectsChangeOrdersChangeOrderIdApprovePost(options)),
  updateMilestone: (options: OptionsOf<typeof generated.updateMilestoneApiV1ProjectsMilestonesMilestoneIdPatch>) =>
    unwrap(generated.updateMilestoneApiV1ProjectsMilestonesMilestoneIdPatch(options)),
  completeMilestone: (options: OptionsOf<typeof generated.completeMilestoneApiV1ProjectsMilestonesMilestoneIdDonePost>) =>
    unwrap(generated.completeMilestoneApiV1ProjectsMilestonesMilestoneIdDonePost(options)),
  createCaseTask: (options: OptionsOf<typeof generated.createCaseTaskApiV1ProjectsTasksPost>) =>
    unwrap(generated.createCaseTaskApiV1ProjectsTasksPost(options)),
  updateTask: (options: OptionsOf<typeof generated.updateTaskApiV1ProjectsTasksTaskIdPatch>) =>
    unwrap(generated.updateTaskApiV1ProjectsTasksTaskIdPatch(options)),
  tickTask: (options: OptionsOf<typeof generated.tickTaskApiV1ProjectsTasksTaskIdDonePost>) =>
    unwrap(generated.tickTaskApiV1ProjectsTasksTaskIdDonePost(options)),
  createTemplate: (options: OptionsOf<typeof generated.createTemplateApiV1ProjectsTemplatesPost>) =>
    unwrap(generated.createTemplateApiV1ProjectsTemplatesPost(options)),
  importTemplate: (options: OptionsOf<typeof generated.importTemplateApiV1ProjectsTemplatesImportPost>) =>
    unwrap(generated.importTemplateApiV1ProjectsTemplatesImportPost(options)),
  saveTemplate: (options: OptionsOf<typeof generated.saveTemplateApiV1ProjectsTemplatesTemplateIdPut>) =>
    unwrap(generated.saveTemplateApiV1ProjectsTemplatesTemplateIdPut(options)),
  /** Export is YAML, not JSON, so it cannot go through `unwrap`. */
  exportTemplateText: async (templateId: string, signal?: AbortSignal): Promise<string> => {
    const result = await generated.exportTemplateApiV1ProjectsTemplatesTemplateIdExportGet({
      path: { template_id: templateId },
      parseAs: 'text',
      signal,
    })
    if (result.error === undefined && typeof result.data === 'string') return result.data
    const status = result.response?.status ?? 0
    throw new ApiError(status, 'template_export', 'Template export did not return YAML.')
  },
  proposeApplyTemplate: (options: OptionsOf<typeof generated.proposeApplyTemplateApiV1ProjectsProjectIdApplyTemplatePost>) =>
    unwrap(generated.proposeApplyTemplateApiV1ProjectsProjectIdApplyTemplatePost(options)),
  createBudgetLine: (options: OptionsOf<typeof generated.createBudgetLineApiV1ProjectsProjectIdBudgetLinesPost>) =>
    unwrap(generated.createBudgetLineApiV1ProjectsProjectIdBudgetLinesPost(options)),
  proposeCancel: (options: OptionsOf<typeof generated.proposeCancelApiV1ProjectsProjectIdCancelPost>) =>
    unwrap(generated.proposeCancelApiV1ProjectsProjectIdCancelPost(options)),
  createChangeOrder: (options: OptionsOf<typeof generated.createChangeOrderApiV1ProjectsProjectIdChangeOrdersPost>) =>
    unwrap(generated.createChangeOrderApiV1ProjectsProjectIdChangeOrdersPost(options)),
  proposeClose: (options: OptionsOf<typeof generated.proposeCloseApiV1ProjectsProjectIdClosePost>) =>
    unwrap(generated.proposeCloseApiV1ProjectsProjectIdClosePost(options)),
  proposeAddDependency: (options: OptionsOf<typeof generated.proposeAddDependencyApiV1ProjectsProjectIdDependenciesPost>) =>
    unwrap(generated.proposeAddDependencyApiV1ProjectsProjectIdDependenciesPost(options)),
  proposeRemoveDependency: (
    options: OptionsOf<typeof generated.proposeRemoveDependencyApiV1ProjectsProjectIdDependenciesDependencyIdRemovePost>,
  ) => unwrap(generated.proposeRemoveDependencyApiV1ProjectsProjectIdDependenciesDependencyIdRemovePost(options)),
  proposeLinkCase: (options: OptionsOf<typeof generated.proposeLinkCaseApiV1ProjectsProjectIdLinkedCasesPost>) =>
    unwrap(generated.proposeLinkCaseApiV1ProjectsProjectIdLinkedCasesPost(options)),
  proposeUnlinkCase: (options: OptionsOf<typeof generated.proposeUnlinkCaseApiV1ProjectsProjectIdLinkedCasesCaseIdUnlinkPost>) =>
    unwrap(generated.proposeUnlinkCaseApiV1ProjectsProjectIdLinkedCasesCaseIdUnlinkPost(options)),
  createMilestone: (options: OptionsOf<typeof generated.createMilestoneApiV1ProjectsProjectIdMilestonesPost>) =>
    unwrap(generated.createMilestoneApiV1ProjectsProjectIdMilestonesPost(options)),
  proposeReopen: (options: OptionsOf<typeof generated.proposeReopenApiV1ProjectsProjectIdReopenPost>) =>
    unwrap(generated.proposeReopenApiV1ProjectsProjectIdReopenPost(options)),
  proposeReplan: (options: OptionsOf<typeof generated.proposeReplanApiV1ProjectsProjectIdReplanPost>) =>
    unwrap(generated.proposeReplanApiV1ProjectsProjectIdReplanPost(options)),
  proposeAddSite: (options: OptionsOf<typeof generated.proposeAddSiteApiV1ProjectsProjectIdSitesPost>) =>
    unwrap(generated.proposeAddSiteApiV1ProjectsProjectIdSitesPost(options)),
  proposeSetState: (options: OptionsOf<typeof generated.proposeSetStateApiV1ProjectsProjectIdStatePost>) =>
    unwrap(generated.proposeSetStateApiV1ProjectsProjectIdStatePost(options)),
  createTask: (options: OptionsOf<typeof generated.createTaskApiV1ProjectsProjectIdTasksPost>) =>
    unwrap(generated.createTaskApiV1ProjectsProjectIdTasksPost(options)),
  proposeUpdate: (options: OptionsOf<typeof generated.proposeProjectUpdateApiV1ProjectsProjectIdUpdatesPost>) =>
    unwrap(generated.proposeProjectUpdateApiV1ProjectsProjectIdUpdatesPost(options)),
  proposeScheduleMeeting: (options: OptionsOf<typeof generated.proposeScheduleMeetingApiV1CasesCaseIdMeetingsPost>) =>
    unwrap(generated.proposeScheduleMeetingApiV1CasesCaseIdMeetingsPost(options)),
  proposeCompleteMeetingMilestone: (
    options: OptionsOf<typeof generated.proposeCompleteMilestoneApiV1MeetingsMeetingIdCompleteMilestonePost>,
  ) => unwrap(generated.proposeCompleteMilestoneApiV1MeetingsMeetingIdCompleteMilestonePost(options)),
  uploadCaseFile: (options: OptionsOf<typeof generated.uploadCaseFileApiV1CasesCaseIdFilesPost>) =>
    unwrap(generated.uploadCaseFileApiV1CasesCaseIdFilesPost(options)),
  downloadCaseFile: (options: OptionsOf<typeof generated.downloadCaseFileApiV1CaseFilesCaseFileIdDownloadGet>) =>
    unwrap(generated.downloadCaseFileApiV1CaseFilesCaseFileIdDownloadGet(options)),
  proposeVendorOrder: (options: OptionsOf<typeof generated.proposeVendorOrderApiV1HardwareOrdersHardwareOrderIdVendorOrderPost>) =>
    unwrap(generated.proposeVendorOrderApiV1HardwareOrdersHardwareOrderIdVendorOrderPost(options)),
  proposeVendorContact: (options: OptionsOf<typeof generated.proposeVendorContactApiV1VendorContactsPost>) =>
    unwrap(generated.proposeVendorContactApiV1VendorContactsPost(options)),
  confirmCard: (cardId: string) =>
    unwrap(
      generated.confirmApiV1CardsCardIdConfirmPost({
        path: { card_id: cardId },
        body: { acknowledged_warnings: [] },
      }),
    ),
  dismissCard: (cardId: string, reason: string | null = null) =>
    unwrap(generated.dismissApiV1CardsCardIdDismissPost({ path: { card_id: cardId }, body: { reason } })),
}

export type {
  CardOut,
  CaseFileOut,
  GanttOut,
  MeetingOut,
  PageProjectRow,
  PlanOut,
  PortfolioFooter,
  ProjectHealth,
  ProjectLanesOut,
  ProjectPlatform,
  ProjectRow,
  ProjectState,
} from '../../lib/api/generated'
